import React, { useContext, useState, useEffect } from 'react';
import { Context as WorldReadyContext, Message } from '@paypalcorp/worldready-react';
import { BodyText, Link, RadioButton, Sheet, TextArea } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types';
import { MessageFormat } from '@paypalcorp/worldready'
import config from '../../../config/config.json';
import {
  trackViewEvent,
  trackMerchantSegmentation,
  trackContinueClick,
  trackCancelClick,
  trackUpgradeLinkClick
} from '../../utils/fptiUtils';

const { deprecationUrls } = config;


const createBoldFormatter = () => {
  const BoldFormatter = (innerMessage) => <b>{innerMessage}</b>;
  BoldFormatter.displayName = 'BoldFormatter';
  return BoldFormatter;
};

// Render deprecation messages
const DeprecationMessages = () => (
  <>
    <BodyText className="deprecation-text">
      <Message id="thirdPartyPerm.deprecationStatement" />
    </BodyText>
    <BodyText className="deprecation-text">
      <Message id="thirdPartyPerm.deprecationSupportEnding" />
    </BodyText>
    <BodyText className="deprecation-text">
      <Message
        id="thirdPartyPerm.deprecationRecommendation"
        link={(innerMessage) => (
          <Link href={deprecationUrls.paypalCheckout}>
            {innerMessage}
          </Link>
        )}
      />
    </BodyText>
    <BodyText className="deprecation-question">
      <Message id="thirdPartyPerm.deprecationApplicationTypePrompt" />
    </BodyText>
  </>
);

// Render description textarea
const DescriptionBox = ({
  id,
  value,
  onChange,
  placeholder,
  showError,
  testId
}) => (
  <div className="deprecation-description-box" data-testid={`${testId}-description-box`}>
    <TextArea
      id={`${id}-description`}
      name={`${id}-description`}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={4}
      data-testid={`${testId}-description-textarea`}
      error={showError}
    />
    {showError && (
      <div className="deprecation-error-text" data-testid="description-error">
        <Message id="thirdPartyPerm.textfielderrortext" />
      </div>
    )}
    <div className="deprecation-helper-text">
      <Message id="thirdPartyPerm.textfieldhelpertext" />
    </div>
  </div>
);

DescriptionBox.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  showError: PropTypes.bool.isRequired,
  testId: PropTypes.string.isRequired,
};

// Render radio option with conditional description
const RadioOption = ({
  id,
  testId,
  selectedOption,
  optionValue,
  labelId,
  description,
  placeholder,
  showDescriptionError,
  onOptionChange,
  onDescriptionChange
}) => (
  <div className="deprecation-radio-option">
    <RadioButton
      id={`${id}-radio`}
      data-testid={`${testId}-radio`}
      value={selectedOption}
      onChange={onOptionChange}
      options={[
        {
          label: (
            <Message id={labelId} bold={createBoldFormatter()} />
          ),
          value: optionValue
        }
      ]}
    />
    {selectedOption === optionValue && (
      <DescriptionBox
        id={id}
        value={description}
        onChange={onDescriptionChange}
        placeholder={placeholder}
        showError={showDescriptionError}
        testId={testId}
      />
    )}
  </div>
);

RadioOption.propTypes = {
  id: PropTypes.string.isRequired,
  testId: PropTypes.string.isRequired,
  selectedOption: PropTypes.string.isRequired,
  optionValue: PropTypes.string.isRequired,
  labelId: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  showDescriptionError: PropTypes.bool.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
};

// Render footer links
const DeprecationLinks = ({ onUpgradeClick, onContinueClick }) => (
  <>
    <div className="deprecation-links">
      <Link
        href={deprecationUrls.upgradeExpressCheckout}
        onClick={onUpgradeClick}
      >
        <Message id="thirdPartyPerm.deprecationppcplink" />
      </Link>
    </div>
    <div className="deprecation-links">
      <Link data-testid="continue-deprecated-credentials" onClick={onContinueClick}>
        <Message id="thirdPartyPerm.deprecationContinueButton" />
      </Link>
    </div>
  </>
);

DeprecationLinks.propTypes = {
  onUpgradeClick: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
};

// Main component
const DeprecationWarning = (props) => {
  const worldReady = useContext(WorldReadyContext);
  const [selectedOption, setSelectedOption] = useState('');
  const [description, setDescription] = useState('');
  const [showRadioError, setShowRadioError] = useState(false);
  const [showDescriptionError, setShowDescriptionError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track VIEW event when popup displays
  useEffect(() => {
    trackViewEvent(props.userDetails?.payerID);
  }, [props.userDetails?.payerID]);

  const handleOptionChange = (e) => {
    const { value } = e.target;
    setSelectedOption(value);
    setDescription('');
    setShowRadioError(false);
    setShowDescriptionError(false);
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    setShowDescriptionError(false);
  };

  const handleContinue = () => {
    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    if (!selectedOption) {
      setShowRadioError(true);
      return;
    }

    if (selectedOption && !description.trim()) {
      setShowDescriptionError(true);
      return;
    }

    // Set submitting state to prevent duplicate clicks
    setIsSubmitting(true);

    if (props.setShowDeprecationWarning) {
      props.setShowDeprecationWarning(false);
    }

    if (props.setGrantOpen) {
      props.setGrantOpen({ type: 'grant', value: true });
    }

    // Track analytics
    trackMerchantSegmentation(props.userDetails?.payerID, selectedOption, description);
    trackContinueClick(props.userDetails?.payerID, selectedOption, description);
  };

  const handleUpgradeExpressCheckoutClick = () => {
    trackUpgradeLinkClick(props.userDetails?.payerID, deprecationUrls.upgradeExpressCheckout);
  };

  const handleSheetClose = () => {
    trackCancelClick(props.userDetails?.payerID);

    if (props.setShowDeprecationWarning) {
      props.setShowDeprecationWarning(false);
    }

    if (props.setGrantOpen) {
      props.setGrantOpen({ type: 'grant', value: false });
    }
  };

  const directPlaceholder = new MessageFormat(worldReady, {
    id: 'thirdPartyPerm.directplaceholdermessage',
  }).format();

  const partnerPlaceholder = new MessageFormat(worldReady, {
    id: 'thirdPartyPerm.partnerplaceholdermessage',
  }).format();

  return (
    <Sheet
      wrapperClassName="sq-modal-wrapper"
      title={
        <div className="deprecation_heading">
          <Message id="thirdPartyPerm.deprecationWarningHeading" />
        </div>
      }
      id="thirdparty-sheet"
      isOpen={true}
      onCloseComplete={handleSheetClose}
      className="deprecation-sheet"
      skipFormFocus
    >
      <div
        id="thirdparty-deprecation-warning"
        data-testid="thirdparty-deprecation-warning"
        className="deprecation-container"
      >
        <DeprecationMessages />

        <div className="deprecation-radio-group">
          <RadioOption
            id="direct"
            testId="direct"
            selectedOption={selectedOption}
            optionValue="direct"
            labelId="thirdPartyPerm.direct"
            description={description}
            placeholder={directPlaceholder}
            showDescriptionError={showDescriptionError}
            onOptionChange={handleOptionChange}
            onDescriptionChange={handleDescriptionChange}
          />

          <RadioOption
            id="partner"
            testId="partner"
            selectedOption={selectedOption}
            optionValue="partner"
            labelId="thirdPartyPerm.partner"
            description={description}
            placeholder={partnerPlaceholder}
            showDescriptionError={showDescriptionError}
            onOptionChange={handleOptionChange}
            onDescriptionChange={handleDescriptionChange}
          />
        </div>

        {showRadioError && (
          <div className="deprecation-error-text" data-testid="radio-error">
            <Message id="thirdPartyPerm.radioerror" />
          </div>
        )}

        <DeprecationLinks
          onUpgradeClick={handleUpgradeExpressCheckoutClick}
          onContinueClick={handleContinue}
        />
      </div>
    </Sheet>
  );
};

DeprecationWarning.propTypes = {
  setShowDeprecationWarning: PropTypes.func.isRequired,
  setGrantOpen: PropTypes.func,
  userDetails: PropTypes.object,
};

DeprecationWarning.displayName = 'DeprecationWarning';

export default DeprecationWarning;
